import { FC, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { useInfiniteQuery } from "@tanstack/react-query";

import { Controls, ControlsButton } from "@/components/Controls";
import FullScreenButton from "@/components/FullScreenButton";
import {
  ResponsiveTable,
  SortableTh,
  Table,
  TableContent,
  TableHeader,
  Th,
} from "@/components/Table";
import { ImageDistributionStatus, SortDirection } from "@/utils/enums";
import { SrOnly } from "@/components/Layout/Parts";
import PostsTableRow from "./PostsTableRow";
import {
  FBImageDistributionResource,
  PaginatedResponse,
  SortState,
} from "@/types/resources";
import { api } from "@/utils/api";
import InfiniteLoader from "@/components/InfiniteLoader";

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.DEFAULT,
  property: "created_at",
};

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    font-weight: 700;
    vertical-align: top;
    &:nth-child(1) {
      padding-left: 24px;
      width: 198px;
    }
    &:nth-child(2) {
      width: 25%;
    }
    &:nth-child(3) {
      width: calc(30% - 99px);
    }
    &:nth-child(4) {
      width: 12%;
    }
    &:nth-child(5) {
      width: calc(23% - 99px);
    }
    &:nth-child(6) {
      width: calc(10% - 56px);
    }
    &:last-child {
      min-width: 56px;
      width: 56px;
      z-index: 1;
      position: relative;
    }
  }
`;

const FiltersHolder = styled.div`
  display: flex;
`;

interface Props {
  status?: ImageDistributionStatus;
}

const PostsTable: FC<Props> = ({ status }) => {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const fetchPosts = ({ pageParam = "/v1/distribution/image-posts" }) => {
    return api.get<never, PaginatedResponse<FBImageDistributionResource>>(
      pageParam,
      {
        params: {
          ...(status === ImageDistributionStatus.POSTED && {
            "filter[scheduled]": 0,
            "filter[status]": status,
          }),
          ...(status && {
            "filter[status]": status,
          }),
          ...(status === ImageDistributionStatus.SCHEDULED && {
            "filter[scheduled]": 1,
            "filter[status]": ImageDistributionStatus.POSTED,
          }),
          ...(sort.direction !== "DEFAULT" && {
            sort: `${sort.direction === "DESC" ? "-" : ""}${sort.property}`,
          }),
        },
      }
    );
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    ["fb_image_distribution", { status, sort }],
    fetchPosts,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
      staleTime: 90 * 1000,
    }
  );

  const posts = useMemo(
    () =>
      data?.pages?.reduce<FBImageDistributionResource[]>((acc, curr) => {
        return [...acc, ...curr.data];
      }, []),
    [data]
  );

  return (
    <>
      <TableHeader>
        <Controls>
          <ControlsButton
            active={!status}
            as={Link}
            to={`/general/image-distribution`}
          >
            All
          </ControlsButton>
          <ControlsButton
            active={status === ImageDistributionStatus.POSTED}
            as={Link}
            to={`/general/image-distribution/posted`}
          >
            Posted
          </ControlsButton>
          <ControlsButton
            active={status === ImageDistributionStatus.SCHEDULED}
            as={Link}
            to={`/general/image-distribution/scheduled`}
          >
            Scheduled
          </ControlsButton>
          <ControlsButton
            active={status === ImageDistributionStatus.DRAFT}
            as={Link}
            to={`/general/image-distribution/draft`}
          >
            Draft
          </ControlsButton>
        </Controls>
        <FiltersHolder>
          <FullScreenButton />
        </FiltersHolder>
      </TableHeader>
      <ResponsiveTable>
        <StyledTable>
          <thead>
            <tr>
              <Th>Image</Th>
              <Th>Post</Th>
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="post_date"
                title="Post Date"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="status"
                title="Status"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="channel"
                title="Facebook Page"
              />
              <Th>Facebook Link</Th>
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!posts?.length}
            >
              {posts?.map((post) => (
                <PostsTableRow key={post.id} post={post} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default PostsTable;
